import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as actions from 'redux/actions/sessionActions';
import { Translate, I18n } from 'react-redux-i18n';
import alertify from 'alertifyjs';
import 'assets/css/two-factor.css';

const QR_API = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=';

function getPanelClasses(variant) {
  if (variant === 'account') {
    return {
      panel: 'account-panel',
      title: 'account-panel-title',
      subtitle: 'account-panel-subtitle',
    };
  }
  return {
    panel: 'settings-panel',
    title: 'settings-panel-title',
    subtitle: 'settings-panel-subtitle',
  };
}

const TwoFactor = (props) => {
  const { variant = 'settings' } = props;
  const classes = getPanelClasses(variant);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [method, setMethod] = useState('email');
  const [setupCode, setSetupCode] = useState('');
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await props.load2faStatus();
      setStatus(data);
      if (data.enabled) {
        setOptIn(true);
      } else {
        setOptIn(!!data.pendingSetup);
      }
    } catch (e) {
      alertify.error(e.message || I18n.t('try_later'));
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const resetSetup = () => {
    setSetupCode('');
    setTotpSetup(null);
    setTotpCode('');
    setEmailSent(false);
  };

  const onOptInChange = async (checked) => {
    if (checked) {
      setOptIn(true);
      return;
    }
    setOptIn(false);
    resetSetup();
    if (status?.pendingSetup) {
      setBusy(true);
      try {
        await props.cancel2faSetup();
        await loadStatus();
      } catch (e) {
        alertify.error(e.message || I18n.t('try_later'));
      } finally {
        setBusy(false);
      }
    }
  };

  const onMethodChange = (next) => {
    if (next === 'sms') return;
    setMethod(next);
    resetSetup();
  };

  const startEmail = async () => {
    setBusy(true);
    try {
      await props.setup2faEmail();
      setEmailSent(true);
      await loadStatus();
    } catch (e) {
      alertify.error(e.message || I18n.t('try_later'));
    } finally {
      setBusy(false);
    }
  };

  const startTotp = async () => {
    setBusy(true);
    try {
      const data = await props.setup2faTotp();
      setTotpSetup(data);
      await loadStatus();
    } catch (e) {
      alertify.error(e.message || I18n.t('try_later'));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    const code = method === 'totp' ? totpCode : setupCode;
    if (!code.trim()) return;
    setBusy(true);
    try {
      await props.enable2fa(code.trim());
      resetSetup();
      await loadStatus();
    } catch (e) {
      alertify.error(e.message || I18n.t('try_later'));
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    if (!disablePassword) return;
    setBusy(true);
    try {
      await props.disable2fa(disablePassword);
      setDisablePassword('');
      setOptIn(false);
      resetSetup();
      await loadStatus();
    } catch (e) {
      alertify.error(e.message || I18n.t('try_later'));
    } finally {
      setBusy(false);
    }
  };

  const methodLabel = (m) => {
    if (m === 'totp') return I18n.t('two_factor_method_app');
    if (m === 'email') return I18n.t('two_factor_method_email');
    if (m === 'sms') return I18n.t('two_factor_method_sms');
    return m;
  };

  if (loading) {
    return (
      <div className={classes.panel}>
        <p className="two-factor-hint">
          <Translate value="two_factor_loading" />
        </p>
      </div>
    );
  }

  if (status?.enabled) {
    return (
      <div className={classes.panel}>
        <div className="two-factor-panel-head">
          <h3 className={classes.title}>
            <Translate value="two_factor_title" />
          </h3>
          <span className="two-factor-badge-optional">
            <Translate value="two_factor_optional" />
          </span>
        </div>
        <p className={classes.subtitle}>
          <Translate value="two_factor_subtitle_enabled" />
        </p>
        <div className="two-factor-active-badge">
          <span aria-hidden="true">✓</span>
          {I18n.t('two_factor_active', { method: methodLabel(status.method) })}
        </div>
        <div className="two-factor-field">
          <label htmlFor="disable-pwd">
            <Translate value="password_current" />
          </label>
          <input
            id="disable-pwd"
            type="password"
            autoComplete="current-password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn-outline-danger"
          onClick={onDisable}
          disabled={busy || !disablePassword}
        >
          <Translate value="two_factor_disable" />
        </button>
      </div>
    );
  }

  const qrSrc = totpSetup?.otpauthUrl
    ? `${QR_API}${encodeURIComponent(totpSetup.otpauthUrl)}`
    : '';

  return (
    <div className={classes.panel}>
      <div className="two-factor-panel-head">
        <h3 className={classes.title}>
          <Translate value="two_factor_title" />
        </h3>
        <span className="two-factor-badge-optional">
          <Translate value="two_factor_optional" />
        </span>
      </div>
      <p className={classes.subtitle}>
        <Translate value="two_factor_subtitle" />
      </p>

      <div className="two-factor-off-notice">
        <Translate value="two_factor_not_required" />
      </div>

      <label className="two-factor-opt-in">
        <input
          type="checkbox"
          checked={optIn}
          disabled={busy}
          onChange={(e) => onOptInChange(e.target.checked)}
        />
        <span>
          <Translate value="two_factor_opt_in" />
        </span>
      </label>

      {optIn && (
        <div className="two-factor-settings">
          <p className="two-factor-hint">
            <Translate value="two_factor_intro" />
          </p>

          <div className="two-factor-methods" role="radiogroup" aria-label={I18n.t('two_factor_choose')}>
            <label className={`two-factor-method${method === 'email' ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="twoFactorMethod"
                value="email"
                checked={method === 'email'}
                onChange={() => onMethodChange('email')}
              />
              <span className="two-factor-method-label">
                <Translate value="two_factor_method_email" />
              </span>
            </label>

            <label className="two-factor-method is-disabled" title={I18n.t('two_factor_sms_soon')}>
              <input type="radio" name="twoFactorMethod" value="sms" disabled />
              <span className="two-factor-method-label">
                <Translate value="two_factor_method_sms" />
              </span>
              <span className="two-factor-badge-soon">
                <Translate value="coming_soon" />
              </span>
            </label>

            <label className={`two-factor-method${method === 'totp' ? ' is-selected' : ''}`}>
              <input
                type="radio"
                name="twoFactorMethod"
                value="totp"
                checked={method === 'totp'}
                onChange={() => onMethodChange('totp')}
              />
              <span className="two-factor-method-label">
                <Translate value="two_factor_method_app" />
              </span>
            </label>
          </div>

          {method === 'email' && (
            <div className="two-factor-setup-block">
              <p className="two-factor-hint">
                {status?.hasEmail
                  ? I18n.t('two_factor_email_hint', { email: status.maskedEmail })
                  : I18n.t('two_factor_need_email')}
              </p>
              {!emailSent ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || !status?.hasEmail}
                  onClick={startEmail}
                >
                  <Translate value="two_factor_send_code" />
                </button>
              ) : (
                <>
                  <div className="two-factor-field">
                    <label htmlFor="2fa-email-code">
                      <Translate value="two_factor_code" />
                    </label>
                    <input
                      id="2fa-email-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={setupCode}
                      onChange={(e) => setSetupCode(e.target.value)}
                      placeholder="000000"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !setupCode.trim()}
                    onClick={confirmEnable}
                  >
                    <Translate value="two_factor_activate" />
                  </button>
                </>
              )}
            </div>
          )}

          {method === 'totp' && (
            <div className="two-factor-setup-block">
              <p className="two-factor-hint">
                <Translate value="two_factor_app_hint" />
              </p>
              {!totpSetup ? (
                <button type="button" className="btn btn-primary" disabled={busy} onClick={startTotp}>
                  <Translate value="two_factor_generate_qr" />
                </button>
              ) : (
                <>
                  {qrSrc && (
                    <img
                      src={qrSrc}
                      alt={I18n.t('two_factor_qr_alt')}
                      className="two-factor-qr"
                      width={200}
                      height={200}
                    />
                  )}
                  {totpSetup.secret && (
                    <p className="two-factor-manual-key">
                      <Translate value="two_factor_manual_key" />
                      <code>{totpSetup.secret}</code>
                    </p>
                  )}
                  <div className="two-factor-field">
                    <label htmlFor="2fa-totp-code">
                      <Translate value="two_factor_app_code" />
                    </label>
                    <input
                      id="2fa-totp-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      placeholder="000000"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || !totpCode.trim()}
                    onClick={confirmEnable}
                  >
                    <Translate value="two_factor_activate" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

TwoFactor.propTypes = {
  variant: PropTypes.oneOf(['settings', 'account']),
  load2faStatus: PropTypes.func.isRequired,
  cancel2faSetup: PropTypes.func.isRequired,
  setup2faEmail: PropTypes.func.isRequired,
  setup2faTotp: PropTypes.func.isRequired,
  enable2fa: PropTypes.func.isRequired,
  disable2fa: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  load2faStatus: () => dispatch(actions.load2faStatus()),
  cancel2faSetup: () => dispatch(actions.cancel2faSetup()),
  setup2faEmail: () => dispatch(actions.setup2faEmail()),
  setup2faTotp: () => dispatch(actions.setup2faTotp()),
  enable2fa: (code) => dispatch(actions.enable2fa(code)),
  disable2fa: (password) => dispatch(actions.disable2fa(password)),
});

export default connect(null, mapDispatchToProps)(TwoFactor);
